import { Box, Flex, Grid, Link, Text } from '@chakra-ui/react';
import { CompanySilhouetteSrc } from '@tryprospect/ui/images';
import { Avatar } from '@tryprospect/ui/neuron/components';

import { Pagination } from '@/components/Common/Pagination';
import type { CompanySeoRecord } from '@/utils/seo/company-directory';
import { formatWebsiteDomain } from '@/utils/url';

type Props = {
  companies: CompanySeoRecord[];
  getPageHref?: (page: number) => string;
  hasNextPage?: boolean;
  page: number;
  totalPages?: number;
};

export const DirectoryList = ({ companies, getPageHref, hasNextPage, page, totalPages }: Props) => {
  const defaultGetPageHref = (targetPage: number) => {
    if (targetPage <= 1) {
      return '/companies';
    }

    return `/companies/page/${targetPage}`;
  };

  const pageHref = getPageHref ?? defaultGetPageHref;

  return (
    <Box
      bgColor="white"
      flexWrap="wrap"
      justifyContent="flex-start"
      minHeight={{ base: 'auto', md: '45vh' }}
      py={{ base: '32px', md: '96px' }}
    >
      <Grid
        gridGap={{ base: '24px', md: '32px' }}
        gridTemplateColumns={{
          base: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
        maxWidth="1280px"
        mx="auto"
        paddingX={{ base: '24px', md: '32px' }}
        width="100%"
      >
        {companies.map((company) => (
          <Box
            _hover={{
              boxShadow:
                '0 6px 12px -2px rgba(95, 183, 202, 0.2), 0 4px 8px -2px rgba(95, 183, 202, 0.1)',
              textDecoration: 'none',
            }}
            as={Link}
            bgColor="white"
            border="1px solid"
            borderColor="teal.200"
            borderRadius="12px"
            boxShadow="0 4px 9px -2px rgba(95, 183, 202, 0.14), 0 2px 5px -2px rgba(95, 183, 202, 0.08)"
            href={`/companies/${company.publicId}${company.slug ? `/${company.slug}` : ''}`}
            key={company.publicId}
            overflow="hidden"
            p={{ base: '16px', md: '24px' }}
          >
            <Flex
              alignItems={{ base: 'flex-start', md: 'center' }}
              flexDir={{ base: 'column', md: 'row' }}
              gap="12px"
            >
              <Avatar
                border="none"
                boxShadow="none"
                boxSize={{ base: '40px', md: '48px' }}
                imageProps={{ boxSize: '30px', src: CompanySilhouetteSrc }}
                objectFit="contain"
                src={company.pictureUrl ?? undefined}
              />

              <Box>
                <Text color="teal.500" fontSize="18px" fontWeight="500">
                  {company.name}
                </Text>

                <Text color="teal.300" fontWeight="300" mt="4px">
                  {formatWebsiteDomain(company.website)}&nbsp;
                </Text>
              </Box>
            </Flex>

            {company.industry && (
              <Text color="gray.600" fontSize="14px" mt="16px">
                {company.industry}
              </Text>
            )}
          </Box>
        ))}
      </Grid>

      <Flex
        alignItems="center"
        justifyContent="flex-end"
        maxWidth="1280px"
        mt={{ base: '32px', md: '64px' }}
        mx="auto"
        paddingX={{ base: '24px', md: '32px' }}
      >
        <Pagination
          getPageHref={pageHref}
          hasNextPage={hasNextPage}
          page={page}
          totalPages={totalPages}
        />
      </Flex>
    </Box>
  );
};
